import { JSX, Show, createMemo } from 'solid-js';
import { useQuery } from '@tanstack/solid-query';
import { t } from '@trimble/i18n';

import { useFeatureExtractionContext } from '@mobile-mapping/providers';
import {
  findCadObjectDebugSnapshot,
  stringifyDebugJson,
} from './cad-object-debug-snapshot';

const JSON_INDENT = 2;

export function CadObjectDebugSection(props: {
  readonly featureId: string;
}): JSX.Element {
  const featureExtraction = useFeatureExtractionContext();

  // The CAD model is not reactive; re-read whenever edits rotate the tile cache.
  const snapshot = createMemo(() => {
    featureExtraction.tileCacheToken();
    return findCadObjectDebugSnapshot(props.featureId);
  });

  return (
    <section class="flex flex-col gap-1" data-testid="cad-object-debug-section">
      <h4 class="text-sm font-semibold">
        {t('mobileMapping.cadEditing.propertiesPanel.cadObjectDebugSection')}
      </h4>
      <Show
        when={snapshot()}
        fallback={
          <div class="text-sm">
            {t(
              'mobileMapping.cadEditing.propertiesPanel.cadObjectDebugNotFound'
            )}
          </div>
        }
      >
        {(cadObject) => (
          <pre class="max-h-[400px] overflow-auto whitespace-pre-wrap text-xs">
            {stringifyDebugJson(cadObject(), JSON_INDENT)}
          </pre>
        )}
      </Show>
    </section>
  );
}

export function FeatureServiceDebugSection(props: {
  readonly featureId: string;
}): JSX.Element {
  const featureExtraction = useFeatureExtractionContext();

  const featureQuery = useQuery(() => {
    const service = featureExtraction.featuresApiService();
    const projectId = featureExtraction.scope()?.projectId;
    const featureId = props.featureId;
    return {
      queryKey: ['feature-service-debug', projectId, featureId],
      queryFn: ({ signal }: { signal: AbortSignal }) => {
        if (!service || !projectId) {
          throw new Error('Features API is not available');
        }
        return service.fetchFeatureById(projectId, featureId, {}, signal);
      },
      enabled: service !== undefined && projectId !== undefined,
    };
  });

  return (
    <section
      class="flex flex-col gap-1"
      data-testid="feature-service-debug-section"
    >
      <h4 class="text-sm font-semibold">
        {t(
          'mobileMapping.cadEditing.propertiesPanel.featureServiceDebugSection'
        )}
      </h4>
      <Show
        when={!featureQuery.isError}
        fallback={
          <div class="text-sm">
            {t(
              'mobileMapping.cadEditing.propertiesPanel.featureServiceDebugError',
              {
                message: featureQuery.error?.message ?? '',
              }
            )}
          </div>
        }
      >
        <Show
          when={featureQuery.data}
          fallback={
            <div class="text-sm">
              {t(
                'mobileMapping.cadEditing.propertiesPanel.featureServiceDebugLoading'
              )}
            </div>
          }
        >
          {(data) => (
            <pre class="max-h-[400px] overflow-auto whitespace-pre-wrap text-xs">
              {JSON.stringify(data(), null, JSON_INDENT)}
            </pre>
          )}
        </Show>
      </Show>
    </section>
  );
}
